import { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DOMPurify from 'dompurify';
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import { REVIEW_TYPES, EXPERIMENT_STATES } from "../utils/thorState";
import { serverErrMsg } from "../utils/validators";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import Button from "../../common/elements/Button";

const fmtDate = (dt) => dt ? new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// Mirrors server-side handler validateReview() checks.
// Review types absent from this map are allowed in any experiment state.
const REVIEW_REQUIRED_STATE = {
    engineer_design_review: 'design_review',
    engineer_instrumentation_signoff: 'instrumentation',
    owner_pre_prod_signoff: 'pre_prod_validation',
    engineer_cleanup_signoff: 'cleanup',
};

const viewLabel = (r) => {
    if (r.isApproved == null) return 'Not Yet Provided';
    if (r.previousReviewSubmission == null) return 'Not Available';
    return 'View';
};

const REVIEW_TYPE_OPTIONS = Object.values(REVIEW_TYPES);

// Fix 3 — Fallback review guidelines when the server returns no template
const FALLBACK_GUIDELINES = {
    analyst_design_review: "**Analyst Design Review Checklist**\n\n✓ Hypothesis is well-formed (clear If/Then/Because)\n✓ At least one Primary metric is defined and causally connected to the hypothesis\n✓ Guardrail metrics are appropriate\n✓ Audience description accurately reflects the target population\n✓ Days to Power is set (≥7 days)\n✓ Experiment type and traffic allocation are appropriate",
    engineer_design_review: "**Engineer Design Review Checklist**\n\n✓ Variable keys and values are correctly configured\n✓ Bucket names/keys match code implementation plan\n✓ Traffic allocation is technically feasible\n✓ No conflicts with other running experiments on the same surfaces\n✓ Experiment type is correct",
    engineer_instrumentation_signoff: "**Instrumentation Signoff Checklist**\n\n✓ Experiment is implemented in code (bucket call + exposure event)\n✓ Tracking plan is filled in (sources, pages, pattern)\n✓ Exposure event fires correctly in both DEV and PROD\n✓ No data collection issues found",
    owner_pre_prod_signoff: "**Pre-prod Signoff Checklist**\n\n✓ All design reviews are approved\n✓ Instrumentation is complete and verified\n✓ Experiment is correctly running in DEV\n✓ Stakeholders are notified\n✓ Ready to launch in production",
    engineer_cleanup_signoff: "**Cleanup Signoff Checklist**\n\n✓ Winning variant has been fully shipped (or experiment was rolled back)\n✓ Feature flag code has been cleaned up\n✓ Experiment code removed from production\n✓ Outcome recorded in Thor",
};

// Render fallback guidelines plain text (with \n) as formatted JSX
const FallbackGuidelines = ({ text }) => (
    <div className="content is-small">
        {text.split('\n').map((line, i) => {
            if (!line.trim()) return <br key={i} />;
            if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="has-text-weight-semibold mb-2">{line.replace(/\*\*/g, '')}</p>;
            }
            return <p key={i} className="mb-1">{line}</p>;
        })}
    </div>
);

// ── View previous submission modal ─────────────────────────────────────────

const ViewSubmissionModal = ({ isActive, reviewTypeLabel, content, onClose }) => (
    <div className={`modal ${isActive ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={onClose} />
        <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="view-modal-title" style={{ width: '780px', maxWidth: '95vw' }}>
            <header className="modal-card-head">
                <p id="view-modal-title" className="modal-card-title">{reviewTypeLabel} — Previous Submission</p>
                <button className="delete" onClick={onClose} />
            </header>
            <section className="modal-card-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {content
                    ? <div className="content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
                    : <p className="has-text-grey">No previous submission available.</p>}
            </section>
            <footer className="modal-card-foot">
                <Button onClick={onClose}>Close</Button>
            </footer>
        </div>
    </div>
);

const SubmitReviewModal = ({ isActive, initialReviewType, experimentKey, onClose, onSubmit, isSubmitting, onNavigate }) => {
    const { execute, executeHtml } = useFetchWithMsal();
    const [reviewType, setReviewType] = useState(initialReviewType ?? REVIEW_TYPE_OPTIONS[0]?.value ?? '');
    const [approved, setApproved] = useState(true);
    const [comment, setComment] = useState('');
    const [commentError, setCommentError] = useState('');
    const [template, setTemplate] = useState('');
    const [loadingTemplate, setLoadingTemplate] = useState(false);

    // Reset form + fetch template together so they always use the same reviewType
    useEffect(() => {
        if (!isActive) return;
        // Normalise to lowercase so it matches REVIEW_TYPE_OPTIONS keys and API expectations
        const type = (initialReviewType ?? REVIEW_TYPE_OPTIONS[0]?.value ?? '').toLowerCase();
        setReviewType(type);
        setApproved(true);
        setComment('');
        setCommentError('');
        if (!type || !experimentKey) return;
        setLoadingTemplate(true);
        setTemplate('');
        executeHtml('GET', thor_api_urls.experimentReviewTemplates(experimentKey, type.toLowerCase()))
            .then(text => {
                // API may return plain HTML or JSON { message: "..." }
                try {
                    const parsed = JSON.parse(text);
                    setTemplate(parsed?.message ?? text ?? '');
                } catch {
                    setTemplate(text ?? '');
                }
            })
            .catch(() => setTemplate(''))
            .finally(() => setLoadingTemplate(false));
    }, [isActive, initialReviewType, experimentKey, execute]);

    const reviewTypeLabel = REVIEW_TYPE_OPTIONS.find(r => r.value === reviewType)?.label ?? reviewType;

    const handleApprovedChange = (value) => {
        setApproved(value);
        setCommentError('');
    };

    const handleSubmit = () => {
        if (approved === false && !comment.trim()) {
            setCommentError('A comment is required when rejecting a review.');
            return;
        }
        onSubmit({ reviewType, approved, comment });
    };

    const handleClose = () => {
        setCommentError('');
        onClose();
    };

    // Fix 5 — Quick-links to navigate to relevant tabs without closing the modal
    // TODO: wire up onNavigate from ThorComponent so these links can close the modal
    // and switch tabs simultaneously. For now they close the modal then signal navigation.
    const handleNavLink = (tabName) => {
        handleClose();
        if (onNavigate) onNavigate(tabName);
    };

    const fallback = FALLBACK_GUIDELINES[reviewType?.toLowerCase()];

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={handleClose} />
            <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="submit-modal-title" style={{ width: '900px', maxWidth: '95vw' }}>
                <header className="modal-card-head">
                    <p id="submit-modal-title" className="modal-card-title">{reviewTypeLabel}</p>
                    <button className="delete" onClick={handleClose} />
                </header>
                <section className="modal-card-body" style={{ padding: 0 }}>
                    {/* Fix 5 — Quick-nav links row */}
                    <div style={{ padding: '0.5rem 1.25rem', borderBottom: '1px solid #dbdbdb', background: '#fafafa', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="has-text-grey is-size-7">Jump to:</span>
                        {/* TODO: pass onNavigate prop from ThorComponent to enable seamless tab switching */}
                        <a className="tag is-link is-light is-small" style={{ cursor: 'pointer' }} onClick={() => handleNavLink('overview')} title="View hypothesis in Overview tab">View Hypothesis ↗</a>
                        <a className="tag is-link is-light is-small" style={{ cursor: 'pointer' }} onClick={() => handleNavLink('metrics')} title="View metrics in Metrics tab">View Metrics ↗</a>
                        <a className="tag is-link is-light is-small" style={{ cursor: 'pointer' }} onClick={() => handleNavLink('audiences')} title="View audiences in Audiences tab">View Audiences ↗</a>
                    </div>
                    <div style={{ display: 'flex', minHeight: '340px' }}>
                        {/* Left — template */}
                        <div style={{ flex: '1 1 55%', padding: '1.25rem', borderRight: '1px solid #dbdbdb', overflowY: 'auto', maxHeight: '60vh' }}>
                            <p className="has-text-weight-semibold is-size-7 has-text-grey mb-2 is-uppercase" style={{ letterSpacing: '0.05em' }}>Review Guidelines</p>
                            {loadingTemplate
                                ? <SmallLoadingSpinner />
                                : template
                                    ? <div className="content is-small" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template) }} />
                                    : fallback
                                        ? <FallbackGuidelines text={fallback} />
                                        : <p className="has-text-grey is-size-7">No guidelines available for this review type.</p>}
                        </div>

                        {/* Right — form */}
                        <div style={{ flex: '0 0 45%', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {!initialReviewType && (
                                <div className="field">
                                    <label className="label is-small">
                                        Review Type<HelpTooltip text={HELP.approvals.reviewType} />
                                    </label>
                                    <div className="select is-fullwidth is-small">
                                        <select value={reviewType} onChange={e => setReviewType(e.target.value)}>
                                            {REVIEW_TYPE_OPTIONS.map(r => (
                                                <option key={r.value} value={r.value}>{r.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                            <div className="field">
                                <label className="label is-small">
                                    Decision<HelpTooltip text={HELP.approvals.decision} />
                                </label>
                                <div className="buttons has-addons">
                                    <button type="button"
                                        className={`button ${approved ? 'is-success is-selected' : ''}`}
                                        onClick={() => handleApprovedChange(true)}>Approve</button>
                                    <button type="button"
                                        className={`button ${!approved ? 'is-danger is-selected' : ''}`}
                                        onClick={() => handleApprovedChange(false)}>Reject</button>
                                </div>
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <label className="label is-small">
                                    Comment{!approved && <span className="has-text-danger ml-1">*</span>}
                                    <HelpTooltip text={HELP.approvals.comment} />
                                </label>
                                <div className="control" style={{ height: '100%' }}>
                                    <textarea className={`textarea is-small ${commentError ? 'is-danger' : ''}`}
                                              style={{ height: '100%', minHeight: '120px' }}
                                              value={comment}
                                              onChange={e => { setComment(e.target.value); if (commentError) setCommentError(''); }}
                                              placeholder={approved ? 'Optional comment…' : 'Required: explain why this review is rejected…'} />
                                </div>
                                {commentError && <p className="help is-danger">{commentError}</p>}
                            </div>
                            <div className="buttons mt-2">
                                <Button className="is-link"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting || !reviewType}>
                                    {isSubmitting ? 'Submitting…' : 'Submit'}
                                </Button>
                                <Button onClick={handleClose}>Cancel</Button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

// Infer user role from email — heuristic only, not security-critical
const inferRole = (email) => {
    if (!email) return null;
    const local = email.split('@')[0].toLowerCase();
    if (local.includes('analyst') || local.includes('data_sci') || local.includes('ds')) return 'analyst';
    if (local.includes('engineer') || local.includes('eng') || local.includes('dev')) return 'engineer';
    return null;
};

// Which review types are relevant for each inferred role
const ROLE_REVIEW_TYPES = {
    analyst: new Set(['analyst_design_review']),
    engineer: new Set(['engineer_design_review', 'engineer_instrumentation_signoff', 'engineer_cleanup_signoff']),
};

const getForMeReviews = (allReviews, experimentState, inferredRole) =>
    allReviews.filter(r => {
        const requiredState = REVIEW_REQUIRED_STATE[r.reviewType?.toLowerCase()];
        if (requiredState !== undefined && requiredState !== experimentState) return false;
        if (inferredRole && ROLE_REVIEW_TYPES[inferredRole]) return ROLE_REVIEW_TYPES[inferredRole].has(r.reviewType?.toLowerCase());
        return true;
    });

const ApprovalsTab = ({ experimentKey, experimentState, onNavigate, onStateChange }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();
    const { instance } = useMsal();
    const currentEmail = instance.getAllAccounts()[0]?.username ?? '';
    const inferredRole = inferRole(currentEmail);

    const [reviewsState, setReviewsState] = useState(buildLoadingState([]));
    const [modalOpen, setModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitReviewType, setSubmitReviewType] = useState(null);
    const [viewSubmission, setViewSubmission] = useState(null);
    const [forMeOnly, setForMeOnly] = useState(false);

    const fetchReviews = useCallback(() => {
        setReviewsState(buildLoadingState([]));
        execute('GET', thor_api_urls.experimentReviews(experimentKey))
            .then(data => setReviewsState(buildDataState(data?.reviews ?? (Array.isArray(data) ? data : []))))
            .catch(error => {
                console.error("Failed to fetch reviews:", error);
                setReviewsState(buildErrorState(error, []));
            });
    }, [execute, experimentKey]);

    useEffect(() => { fetchReviews(); }, [fetchReviews]);

    const handleSubmit = ({ reviewType, approved, comment }) => {
        setIsSubmitting(true);
        const body = { reviewType, approved, comment };
        if (reviewType === 'engineer_instrumentation_signoff') body.instrumentationInstructions = {};
        execute('POST', thor_api_urls.experimentReviews(experimentKey), body)
            .then(() => {
                setModalOpen(false);
                fetchReviews();
                addToast('Review submitted.', 'success');
                execute('GET', thor_api_urls.experiment(experimentKey))
                    .then(data => {
                        const detail = data?.experiment ?? data;
                        if (detail?.state && detail.state !== experimentState) {
                            onStateChange?.(detail.state);
                        }
                    })
                    .catch(() => {});
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to submit review.'), 'danger'))
            .finally(() => setIsSubmitting(false));
    };

    const allReviews = reviewsState.data || [];

    const forMeReviews = getForMeReviews(allReviews, experimentState, inferredRole);
    const reviews = forMeOnly ? forMeReviews : allReviews;
    const forMeCount = forMeReviews.length;

    const availableReviewTypes = Object.values(REVIEW_TYPES).filter(rt => {
        const requiredState = REVIEW_REQUIRED_STATE[rt.value];
        return requiredState === undefined || requiredState === experimentState;
    });

    const currentStateName = EXPERIMENT_STATES[experimentState]?.label ?? experimentState ?? 'current state';

    return (
        <>
            {/* Fix 1 — Read-only mode banner: shown when editMode is off and there are reviews available */}
            {!editMode && availableReviewTypes.length > 0 && (
                <div className="notification is-warning is-light mb-4">
                    <span className="icon"><i className="fas fa-lock"></i></span>
                    <strong>Read-only mode</strong> — Click the <strong>Edit</strong> button at the top of the page to submit or view reviews.
                </div>
            )}

            <div className="level mb-3">
                <div className="level-left" style={{ gap: '0.75rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <p className="subtitle is-5 mb-0">
                        Approvals<HelpTooltip text={HELP.approvals.general} />
                    </p>
                    <div className="buttons has-addons" style={{ marginBottom: 0 }}>
                        <button type="button"
                            className={`button is-small ${!forMeOnly ? 'is-link is-selected' : 'is-light'}`}
                            onClick={() => setForMeOnly(false)}>
                            All ({allReviews.length})
                        </button>
                        <button type="button"
                            className={`button is-small ${forMeOnly ? 'is-link is-selected' : 'is-light'}`}
                            onClick={() => setForMeOnly(true)}
                            title="Show only approvals relevant to the current experiment state">
                            For me ({forMeCount})
                        </button>
                    </div>
                </div>
                {availableReviewTypes.length === 0 && (
                    <div className="level-right">
                        <div className="notification is-info is-light" style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', marginBottom: 0, maxWidth: '340px', textAlign: 'right' }}>
                            No approvals required in the current state ({currentStateName}).
                        </div>
                    </div>
                )}
            </div>

            <StateHandler showLoader={reviewsState.loading} showError={reviewsState.error}
                          loadingScreen={<SmallLoadingSpinner />}>
                {reviews.length === 0
                    ? (
                        <div className="has-text-centered" style={{ padding: '2rem', color: '#888' }}>
                            <p className="has-text-weight-semibold mb-2" style={{ color: '#363636' }}>No reviews yet</p>
                            <p className="is-size-7 mb-3">
                                {availableReviewTypes.length > 0
                                    ? <>Reviews for this stage: <strong>{availableReviewTypes.map(r => r.label).join(', ')}</strong>. Enable Edit mode and click "Submit Review" above.</>
                                    : 'Reviews will appear here once the experiment reaches the appropriate state.'}
                            </p>
                        </div>
                    )
                    : (
                        <table className="table is-fullwidth is-striped is-hoverable">
                            <thead>
                                <tr>
                                    <th>Review Type</th>
                                    <th>Decision</th>
                                    <th>Reviewer</th>
                                    <th>Date</th>
                                    <th>Comments</th>
                                    <th className="has-text-centered">Action</th>
                                    <th className="has-text-centered">View</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.map((r, i) => (
                                    <tr key={r.reviewType ?? i}>
                                        <td className="has-text-weight-semibold">{r.reviewTypeDisplayName ?? r.reviewType}</td>
                                        <td>
                                            {r.isApproved == null
                                                ? <span className="tag is-light">Pending</span>
                                                : r.isApproved
                                                    ? <span className="tag is-success">Approved</span>
                                                    : <span className="tag is-danger">Rejected</span>}
                                        </td>
                                        <td>{r.reviewedBy ?? '—'}</td>
                                        <td className="has-text-grey">{fmtDate(r.reviewedAt)}</td>
                                        <td className="has-text-grey" title={r.comments || ''} style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.comments || '—'}
                                        </td>
                                        <td className="has-text-centered">
                                            {(() => {
                                                const requiredState = REVIEW_REQUIRED_STATE[r.reviewType?.toLowerCase()];
                                                const anyState = requiredState === undefined;
                                                const inRightState = anyState || experimentState === requiredState;
                                                const canAct = editMode && inRightState;
                                                const stateLabel = EXPERIMENT_STATES[requiredState]?.label ?? requiredState;
                                                const disabledTitle = !editMode
                                                    ? 'Enable Edit mode to take action'
                                                    : !inRightState
                                                        ? `Requires experiment to be in "${stateLabel}" state`
                                                        : undefined;
                                                return (
                                                    <div>
                                                        <Button
                                                            className={`is-small ${canAct ? 'is-link' : 'is-light'}`}
                                                            disabled={!canAct}
                                                            title={disabledTitle}
                                                            onClick={canAct ? () => { setSubmitReviewType(r.reviewType); setModalOpen(true); } : undefined}>
                                                            Take Action
                                                        </Button>
                                                        <p className="help has-text-grey is-size-7" style={{ marginTop: '0.25rem' }}>
                                                            Requires: {anyState ? 'Any state' : (EXPERIMENT_STATES[requiredState]?.label ?? requiredState)}
                                                        </p>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="has-text-centered">
                                            <Button
                                                className="is-small is-info is-light"
                                                disabled={viewLabel(r) !== 'View'}
                                                onClick={viewLabel(r) === 'View' ? () => setViewSubmission({ label: r.reviewTypeDisplayName ?? r.reviewType, content: r.previousReviewSubmission }) : undefined}>
                                                {viewLabel(r)}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </StateHandler>

            <SubmitReviewModal isActive={modalOpen} isSubmitting={isSubmitting}
                               initialReviewType={submitReviewType}
                               experimentKey={experimentKey}
                               onNavigate={onNavigate}
                               onClose={() => setModalOpen(false)} onSubmit={handleSubmit} />

            <ViewSubmissionModal
                isActive={!!viewSubmission}
                reviewTypeLabel={viewSubmission?.label}
                content={viewSubmission?.content}
                onClose={() => setViewSubmission(null)} />
        </>
    );
};

export default ApprovalsTab;
